import { NOTIFICATION_TYPE, type Notification } from "~/composables/useNotifications";
import useAuthentication from "~/composables/useAuthentication";
import { useUserStore } from "~/stores/useUserStore";
import { GROUP_MEMBER_STATUS } from "~/enums/group.enums";

export interface InvitationAcceptResult {
  token?: string;
  email?: string;
}

export default function useB2BInvitation () {
  const { getAllNotifications, markAsRead } = useNotifications();
  const { checkAuthenticationStatus } = useAuthentication();
  const { acceptGroupInvitation } = useGroup();

  const isInvitationDialogOpen = useState('isInvitationDialogOpen', () => false);
  const autoOpenInvitation = useState<Notification | null>('autoOpenInvitation', () => null);
  const B2B_POPUP_SHOWN_KEY = "b2bPopupShownThisSession";

  const hasPopupBeenShownThisSession = () => {
    return sessionStorage.getItem(B2B_POPUP_SHOWN_KEY) === 'true';
  };

  const markPopupAsShown = () => {
    sessionStorage.setItem(B2B_POPUP_SHOWN_KEY, 'true');
  };

  const findRelevantInvitation = (
    allNotifications: Notification[],
    userGroupUuid?: string,
    userGroupStatus?: string,
  ): Notification | undefined => {
    const pendingInvitations = allNotifications.filter(
      invitation => invitation.data.notificationName === NOTIFICATION_TYPE.B2B_PENDING_GROUP_INVITATION
        && invitation.data.invitationLink,
    );

    if (!pendingInvitations.length) {
      return;
    }

    const validInvitations = [...pendingInvitations].filter(invitation => {
      const invitationGroupUuid = invitation.data.groupUuid;

      if (!userGroupUuid) {
        return true;
      }

      if (invitationGroupUuid === userGroupUuid) {
        return userGroupStatus === GROUP_MEMBER_STATUS.PENDING;
      }

      return false;
    });

    if (!validInvitations.length) {
      return;
    }

    const sortedInvitations = [...validInvitations].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

    if (userGroupUuid && userGroupStatus === GROUP_MEMBER_STATUS.PENDING) {
      const currentGroupInvitation = sortedInvitations.find(
        invitation => invitation.data.groupUuid === userGroupUuid,
      );

      if (currentGroupInvitation) {
        return currentGroupInvitation;
      }
    }

    return sortedInvitations[0];
  };

  const checkAndOpenInvitationDialog = async () => {
    const isAuthenticated = await checkAuthenticationStatus();
    const popupShown = hasPopupBeenShownThisSession();

    if (isInvitationDialogOpen.value || popupShown || !isAuthenticated) {
      return;
    }
    const route = useRoute();
    if (route.path.startsWith('/auth/')) {
      return;
    }

    try {
      const allNotifications = await getAllNotifications();

      if (!allNotifications || allNotifications.length === 0) {
        return;
      }

      // TODO: user store does not belong here
      const { user } = useUserStore();

      const userGroupUuid = user.value?.group?.groupUuid;
      const userGroupStatus = user.value?.group?.memberStatus;

      // Don't show invitation if user is already active in a group
      if (userGroupStatus === GROUP_MEMBER_STATUS.ACTIVE) {
        return;
      }

      const invitationToShow = findRelevantInvitation(allNotifications, userGroupUuid, userGroupStatus);

      if (invitationToShow) {
        markPopupAsShown();
        autoOpenInvitation.value = invitationToShow;
        isInvitationDialogOpen.value = true;
      }
    } catch (error) {
      // Silently fail - dialog won't show
    }
  };

  const getPendingInvitation = async () => {
    try {
      // TODO: user store does not belong here
      const { user } = useUserStore();

      const userGroupUuid = user.value?.group?.groupUuid;
      const userGroupStatus = user.value?.group?.memberStatus;

      const allNotifications = await getAllNotifications();

      if (!allNotifications) {
        return;
      }

      return findRelevantInvitation(allNotifications, userGroupUuid, userGroupStatus);
    } catch (error) {
      return;
    }
  };

  const acceptInvitation = async (invitationLink: string) => {
    const url = new URL(invitationLink, window.location.origin);
    const params = new URLSearchParams(url.search);

    const pathParts = url.pathname.split('/').filter(p => p);
    const groupId = pathParts[1];

    if (!groupId) {
      const { $sentry } = useNuxtApp();
      const { dispatchMessage } = useSystemMessages();

      $sentry?.captureException(new Error(`Invalid B2B invitation link: ${invitationLink}`));
      dispatchMessage('Accepting group invitation failed', 'error', 'Invalid invitation link.');

      return;
    }

    const expires = params.get('expires') || '';
    const token = params.get('token') || '';
    const user = params.get('user') || '';
    const signature = params.get('signature') || '';

    const result = await acceptGroupInvitation(groupId, expires, token, user, signature);

    try {
      const allNotifications = await getAllNotifications();
      const groupNotifications = allNotifications?.filter(
        invitation => invitation.data.notificationName === NOTIFICATION_TYPE.B2B_PENDING_GROUP_INVITATION
          && invitation.data.groupUuid === groupId,
      ) || [];

      await Promise.all(groupNotifications.map(invitation => markAsRead(invitation.id)));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }

    return result;
  };

  const handleInvitationAccepted = async (result: InvitationAcceptResult | null) => {
    if (result?.token && result?.email) {
      await navigateTo(
        `/auth/reset-password?token=${result.token}&email=${encodeURIComponent(result.email)}&passwordExists=false`);
      return;
    }

    await navigateTo('/dashboard');
  };

  return {
    checkAndOpenInvitationDialog,
    getPendingInvitation,
    acceptInvitation,
    isInvitationDialogOpen,
    autoOpenInvitation,
    handleInvitationAccepted,
  };
}
